import React, { useMemo, useEffect, useState } from 'react';
import { FiCalendar, FiMousePointer, FiEdit3, FiDownload, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { EmptyState } from '@/components/custom/empty-state';
import { Filter, FilterActions, FilterContent, FilterInput, FilterSelectNative, FilterSelectSearch } from '@/components/custom/filter';
import { EssayMonitoringPlayfulCard } from './essay-monitoring-card';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import moment from 'moment';
import { statusBadgeText } from '../../utils/BadgeEssay';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import { useSession } from '@/hooks/useSession';
import { EssayMonitoringPlayfulCardMobile } from './essay-monitoring-card-mobile';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import EndScroll from '@/components/custom/end-scroll/end-scroll';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import { Badge } from '@/components/ui/badge';
import { DashboardContent } from '@/layouts/dashboard';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead } from '@/components/ui/table';

// Mesma paleta soft usada nas demais telas de redação do professor (Playful) —
// duplicado de propósito, cada tela mantém seu próprio mapeamento visual.
const STATUS_BADGE_VARIANT = {
  accepting_proposal: 'info-soft',
  processing: 'amber-soft',
  reviewing: 'amber-soft',
  ocr_review: 'amber-soft',
  sending_by_teacher: 'amber-soft',
  unreviewed: 'success-soft',
  reviewed: 'success-soft',
  annulled: 'danger-soft',
  annulled_by_teacher: 'danger-soft',
  minimum_length_not_satisfied: 'danger-soft',
  failed_to_process: 'danger-soft',
  close_for_submissions: 'muted',
  not_sent: 'muted'
};

export function EssayMonitoringPlayfulTeacher({ essayId, uri }) {
  const [essay, setEssay] = useState({});
  const [loading, setLoading] = useState(false);
  const [webhookNotification, setWebhookNotification] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const { session } = useSession();
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const client = useClient();

  const filters = useFormik({
    initialValues: {
      status: 'blank',
      searchTerm: '',
      institution: null,
      course: null,
      classroom: null
    }
  });

  useEffect(() => {
    if (isStudent) {
      window.location.href = `/redacao/sobre/${essayId}`;
    }
  }, []);

  // Memoiza os filtros para evitar re-criação a cada render
  const memoizedFilters = useMemo(
    () => ({
      ...(filters.values.institution?.value &&
        filters.values.institution.value !== 'blank' && {
          institution_id: filters.values.institution.value
        }),
      ...(filters.values.course?.value &&
        filters.values.course.value !== 'blank' && {
          course_id: filters.values.course.value
        }),
      ...(filters.values.classroom?.value &&
        filters.values.classroom.value !== 'blank' && {
          classroom_id: filters.values.classroom.value
        }),
      ...(filters.values.status &&
        filters.values.status !== 'blank' && {
          status: filters.values.status
        })
    }),
    [filters.values.institution?.value, filters.values.course?.value, filters.values.classroom?.value, filters.values.status]
  );

  // Hook de infinite scroll com paginação virtual
  const {
    data: essayStudents,
    loading: dataLoading,
    initialLoading,
    hasMore,
    loadingNextPage, // Estado para mostrar loader ao carregar próxima página
    setData: setEssayStudents,
    sentinelRef
  } = useInfiniteScroll({
    endpoint: `essays/${essayId}/essay_students`,
    pageSize: 50,
    filters: memoizedFilters,
    searchTerm: filters.values.searchTerm,
    searchFields: ['user.name', 'user.email'],
    debounceMs: 300,
    onError: () => toast.error('Erro ao buscar estudantes')
  });

  const getEssay = async () => {
    const { data, error } = await client.fetch(['essays', essayId]);
    if (error) {
      toast.error('Erro ao buscar redação');
    } else {
      setEssay(data);
    }
  };

  const fetchInstitutions = async (url = '/institutions') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar instituições');
    } else {
      setInstitutions(data.map(item => ({ value: item.id, label: item.name })));
    }
  };

  const fetchCourses = async (url = '/courses/simple') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar cursos');
    } else {
      setCourses(data.map(item => ({ value: item.id, label: item.title })));
    }
  };

  const fetchClassrooms = async (url = '/classrooms/simple') => {
    if (filters.values.course !== null && filters.values.course.value !== 'blank' && filters.values.institution !== null && filters.values.institution.value !== 'blank') {
      let endpoint = `/classrooms/simple?filter[course_id]=${filters?.values?.course?.value}&filter[institution_id]=${filters?.values?.institution?.value}`;

      const { data, error } = await client.fetch(endpoint);
      if (error) {
        toast.error('Falha ao carregar turmas');
      } else {
        setClassrooms(data.map(item => ({ value: item.id, label: item.title })));
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([getEssay(), fetchInstitutions(), fetchCourses()]).finally(() => {
      setLoading(false);
    });
  }, []);

  const options = [
    { name: 'Correção por Professor', id: 'reviewed' },
    { name: 'Correção por IA', id: 'unreviewed' },
    { name: 'Em revisão', id: 'reviewing' },
    { name: 'Em processamento', id: 'processing' },
    { name: 'Anulada pela IA', id: 'annulled' },
    { name: 'Anulada pelo professor', id: 'annulled_by_teacher' },
    { name: 'Aluno não enviou', id: 'not_sent' },
    { name: 'Aceitando respostas', id: 'accepting_proposal' },
    { name: 'Arquivo inválido', id: 'minimum_length_not_satisfied' },
    { name: 'Falha ao processar', id: 'failed_to_process' },
    { name: 'Prazo encerrado', id: 'close_for_submissions' },
    { name: 'Aguardando envio do texto', id: 'ocr_review' }
  ];

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: uri,
        name: essay?.topic?.length > 70 ? essay?.topic?.slice(0, 70) + '...' : essay?.topic,
        isActive: true
      }
    ]
  };

  useEffect(() => {
    if (!webhookNotification) return;

    const setNewState = (item, socket) => {
      if ((socket?.id && item?.id === socket?.id) || item?.user?.id === socket?.user_id) {
        return { ...item, status: socket?.status, corrector: socket?.corrector, grade: socket?.grade, id: socket?.id };
      }
      return item;
    };

    // Usa função callback para acessar TODOS os dados (allData do hook) — o
    // prevAllData contém os registros completos, não apenas os exibidos, então
    // isso evita sobrescrever o array completo com apenas os dados paginados.
    setEssayStudents(prevAllData => {
      return prevAllData.map(item => setNewState(item, webhookNotification));
    });
    setWebhookNotification(null);
  }, [webhookNotification, setEssayStudents]);

  const handleReceivedNotification = data => {
    setWebhookNotification(data);
  };

  const handleSort = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = useMemo(() => {
    const sortableUsers = [...essayStudents];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'name':
            aValue = a.user.name ?? '';
            bValue = b.user.name ?? '';
            break;
          case 'entrega':
            aValue = new Date(a['delivery-date']) ?? new Date(0);
            bValue = new Date(b['delivery-date']) ?? new Date(0);
            break;
          case 'status':
            aValue = a.status ?? '';
            bValue = b.status ?? '';
            break;
          case 'nota':
            aValue = a.grade ?? 0;
            bValue = b.grade ?? 0;
            break;
          case 'corrigidoPor':
            aValue = a?.corrector?.name ?? '';
            bValue = b?.corrector?.name ?? '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableUsers;
  }, [sortConfig, essayStudents]);

  useEffect(() => {
    fetchClassrooms();
  }, [filters.values.course]);

  function downloadFile() {
    const url = `${process.env.REACT_APP_DEV_URL}/reports/essays/${essayId}/report.xlsx?`;
    let params = new URLSearchParams();
    if (filters.values.classroom) {
      params.set('classroom_id', filters.values.classroom.value);
    }
    if (filters.values.institution) {
      params.set('institution_id', filters.values.institution.value);
    }
    if (filters.values.course) {
      params.set('course_id', filters.values.course.value);
    }

    fetch(`${url}${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${session?.user?.authToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          toast.error(`Erro ao baixar o relatório`);
        }
        return response.blob();
      })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `detalhamento-${essay?.topic}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(error => console.error('Erro no download:', error));
  }

  const summaryWidgets = [
    {
      title: 'Índice de envio',
      value: essay?.['submissions-percent'],
      icon: <FiMousePointer />
    },
    {
      title: 'Nota média',
      value: essay?.['average-grade'],
      icon: <FiCalendar />
    },
    {
      title: 'Prazo de entrega',
      value: essay?.['delivery-date'] ? moment(essay['delivery-date']).format('DD/MM/YYYY') : '--/--/----',
      icon: <FiCalendar />
    },
    ...(essay?.['allow-multiple-submissions']
      ? [
          {
            title: 'Envio por estudante',
            value: '2 envios',
            icon: <FiEdit3 />
          }
        ]
      : [])
  ];

  const publishScheduled = moment(essay?.['publish-at']).isAfter(moment());
  const headerBadgeText = publishScheduled ? 'Agendada' : statusBadgeText[essay.status];
  const headerBadgeVariant = publishScheduled ? 'amber-soft' : STATUS_BADGE_VARIANT[essay.status];

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <ActionCableConsumer
        channel="EssayStudentChannel"
        setObjectChannel={handleReceivedNotification}
        channelKey={'essay_id'}
        channelId={essayId}
      />

      <section
        className="relative mb-6 flex flex-col rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card md:p-8"
        style={{ animation: 'trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) 150ms both' }}
      >
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1
              className="mb-0 line-clamp-2 text-xl font-extrabold leading-snug"
              style={{ color: '#222731' }}
              title={essay?.topic}
            >
              {essay?.topic?.length > 70 ? essay?.topic?.slice(0, 70) + '...' : essay?.topic}
            </h1>

            {headerBadgeText && (
              <Badge
                variant={headerBadgeVariant}
                className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold normal-case"
              >
                {headerBadgeText}
              </Badge>
            )}
          </div>

          <PlayfulCardButton
            onClick={() => setShowAbout(true)}
            className="h-9 w-auto shrink-0 px-5"
          >
            Sobre
          </PlayfulCardButton>
        </div>

        <div className="relative mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {summaryWidgets.map((widget, index) => (
            <div
              key={`widget-${index}`}
              className="flex flex-col gap-1 rounded-[16px] border border-neutral-100 bg-white p-4"
            >
              <span className="text-xs font-bold uppercase text-neutral-400">{widget.title}</span>
              <span
                className="flex items-center gap-1.5 text-lg font-semibold"
                style={{ color: '#222731' }}
              >
                <span className="text-mission">{widget.icon}</span>
                {widget.value ?? '--'}
              </span>
            </div>
          ))}
        </div>

        <div className="relative mt-6">
          <span className="text-xs font-bold uppercase text-neutral-400">Envios: {`${essay?.['submissions-count'] >= 0 ? essay?.['submissions-count'] : '--'}/${essay?.['total-of-students'] >= 0 ? essay?.['total-of-students'] : '--'}`}</span>

          <div className="mt-2 flex flex-wrap items-center gap-3">
            <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-bg-lilac)]">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.min(essay?.['submissions-percent'] ?? 0, 100)}%`, background: 'var(--gradient-progress-bar)' }}
              />
            </div>
          </div>
        </div>
      </section>

      <section
        className="relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card md:p-8"
        style={{ animation: 'trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) 220ms both' }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px]"
          style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
        />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <Filter variant="plain">
            <FilterContent>
              <FilterInput
                label={t('textsCommon.search')}
                placeholder="Pesquisar por estudante"
                type="search"
                name="search"
                value={filters.values.searchTerm}
                onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
              />

              <FilterSelectNative
                blankLabel={t('filter.blankLabelAll2')}
                label={t('filter.labelSelectStatus')}
                value={filters.values.status}
                onChange={e => filters.setFieldValue('status', e.target.value)}
                options={options}
              />

              <FilterSelectSearch
                label="Instituição"
                options={institutions}
                value={filters.values.institution ?? { value: 'blank', label: 'Todos' }}
                setValue={selectedOption => {
                  const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;

                  filters.setFieldValue('institution', normalizedValue);
                  filters.setFieldValue('course', null);
                  filters.setFieldValue('classroom', null);
                }}
              />

              <FilterSelectSearch
                label="Curso"
                options={courses}
                value={filters.values.course ?? { value: 'blank', label: 'Todos' }}
                setValue={selectedOption => {
                  const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;

                  filters.setFieldValue('course', normalizedValue);
                  filters.setFieldValue('classroom', null);
                }}
              />

              <FilterSelectSearch
                label="Turma"
                options={classrooms}
                value={filters.values.classroom ?? { value: 'blank', label: 'Todos' }}
                setValue={selectedOption => {
                  const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;

                  filters.setFieldValue('classroom', normalizedValue);
                }}
              />
            </FilterContent>

            <FilterActions>
              <PlayfulCardButton
                variant="outline"
                icon={FiDownload}
                onClick={downloadFile}
                className="h-9 w-auto shrink-0 px-5"
              >
                Exportar relatório
              </PlayfulCardButton>
            </FilterActions>
          </Filter>
        </div>

        {initialLoading ? (
          <Loader />
        ) : !dataLoading && sortedUsers.length === 0 ? (
          <EmptyState
            type="content"
            title={t('emptyState.noItemFound')}
          />
        ) : (
          <div className="relative">
            <div className="flex flex-col gap-5 md:hidden">
              {sortedUsers?.map(student => (
                <EssayMonitoringPlayfulCardMobile
                  currentUser={session.user.id}
                  essay={essay}
                  key={student?.user?.id}
                  student={student}
                />
              ))}
            </div>

            <Table className="hidden min-w-[1200px] md:table">
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort('name')}
                    style={{ fontWeight: sortConfig.key === 'name' ? 'bold' : 'normal' }}
                  >
                    Nome {sortConfig.key === 'name' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                  </TableHead>
                  <TableHead className="w-[80px]">Envios</TableHead>
                  <TableHead
                    className="w-[116px] cursor-pointer"
                    onClick={() => handleSort('entrega')}
                    style={{ fontWeight: sortConfig.key === 'entrega' ? 'bold' : 'normal' }}
                  >
                    Entrega {sortConfig.key === 'entrega' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                  </TableHead>
                  <TableHead
                    className="w-[82px] cursor-pointer"
                    onClick={() => handleSort('nota')}
                    style={{ fontWeight: sortConfig.key === 'nota' ? 'bold' : 'normal' }}
                  >
                    Nota {sortConfig.key === 'nota' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                  </TableHead>
                  <TableHead
                    className="w-[180px] cursor-pointer"
                    onClick={() => handleSort('status')}
                    style={{ fontWeight: sortConfig.key === 'status' ? 'bold' : 'normal' }}
                  >
                    Status da correção {sortConfig.key === 'status' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                  </TableHead>
                  <TableHead
                    className="w-[226px] cursor-pointer"
                    onClick={() => handleSort('corrigidoPor')}
                    style={{ fontWeight: sortConfig.key === 'corrigidoPor' ? 'bold' : 'normal' }}
                  >
                    Corrigido por {sortConfig.key === 'corrigidoPor' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                  </TableHead>
                  <TableHead className="w-[370px]">Ações</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {sortedUsers?.map((student, i) => (
                  <EssayMonitoringPlayfulCard
                    currentUser={session.user.id}
                    essay={essay}
                    key={i}
                    student={student}
                  />
                ))}
              </TableBody>
            </Table>

            <div
              ref={sentinelRef}
              className="my-5 flex h-5 items-center justify-center"
            >
              {(dataLoading || loadingNextPage) && <Loader />}
            </div>

            {!hasMore && !loadingNextPage && sortedUsers.length > 0 && <EndScroll />}
          </div>
        )}
      </section>

      <Dialog
        open={showAbout}
        onOpenChange={setShowAbout}
      >
        <DialogContent size="md">
          <DialogTitle>Sobre a redação</DialogTitle>

          <div className="flex flex-col gap-1">
            <h3
              className="mb-0 text-sm font-bold uppercase text-neutral-400"
              style={{ color: '#222731' }}
            >
              Enunciado
            </h3>
            <DialogDescription className="whitespace-pre-wrap">{essay?.statement}</DialogDescription>
            <DialogDescription>Tema: {essay?.topic}</DialogDescription>
          </div>

          <div className="flex flex-col gap-4">
            <h3
              className="mb-0 text-sm font-bold uppercase text-neutral-400"
              style={{ color: '#222731' }}
            >
              Textos motivadores
            </h3>

            {essay?.['essay-motivator-texts']?.map((item, index) => (
              <article key={index}>
                <h4 className="mb-1 text-sm font-bold text-neutral-500">{`Texto ${index + 1}`}</h4>
                <div
                  className="course-about__description"
                  dangerouslySetInnerHTML={{ __html: item.body }}
                />
              </article>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
